import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ShieldAlert, X, Banknote, Check, Ban, DollarSign, Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getAdminWithdrawals, decideWithdrawal, createPayout, getAdminArtists, getSettings, updateSettings } from "@/lib/afmApi";
import { fmtUSD, fmtDate } from "@/lib/format";
import { Loading, SectionTitle } from "@/components/common";

const FILTERS = [
  { key: "", label: "Todos" },
  { key: "pending", label: "Pendientes" },
  { key: "approved", label: "Aprobados" },
  { key: "paid", label: "Pagados" },
  { key: "rejected", label: "Rechazados" },
];
const STATUS = {
  pending: { label: "Pendiente", cls: "border-amber-300 bg-amber-50 text-amber-800" },
  approved: { label: "Aprobado", cls: "border-blue-300 bg-blue-50 text-blue-700" },
  paid: { label: "Pagado", cls: "border-emerald-300 bg-emerald-50 text-emerald-700" },
  rejected: { label: "Rechazado", cls: "border-red-300 bg-red-50 text-red-700" },
};
const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-black";

function PayoutDialog({ viewerId, onClose, onDone }) {
  const [artists, setArtists] = useState([]);
  const [userId, setUserId] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { getAdminArtists(viewerId).then((d) => setArtists(d?.artists || [])).catch(() => {}); }, [viewerId]);

  const submit = async () => {
    if (!userId || Number(amount) <= 0) { toast.error("Elige artista y monto válido"); return; }
    setSaving(true);
    try {
      await createPayout({ viewer_id: viewerId, user_id: userId, amount: Number(amount), note: note.trim() });
      toast.success("Pago manual registrado");
      onDone(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo registrar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" data-testid="payout-dialog">
      <div className="my-8 w-full max-w-md rounded-2xl border border-slate-300 bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 className="font-serif text-xl font-bold text-black">Pago manual</h2>
          <button onClick={onClose} data-testid="close-payout"><X className="h-5 w-5 text-slate-400 hover:text-black" /></button>
        </div>
        <div className="space-y-4 p-6">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Artista</label>
            <select data-testid="payout-artist" value={userId} onChange={(e) => setUserId(e.target.value)} className={inputCls}>
              <option value="">Selecciona…</option>
              {artists.map((a) => <option key={a.user_id} value={a.user_id}>{a.name} · disp {fmtUSD(a.available_usd)}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Monto (USD)</label>
            <input data-testid="payout-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Nota</label>
            <input data-testid="payout-note" value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} />
          </div>
        </div>
        <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-4">
          <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium hover:border-black">Cancelar</button>
          <button onClick={submit} disabled={saving} data-testid="submit-payout" className="rounded-lg bg-black px-5 py-2 text-sm font-semibold text-white disabled:opacity-50">{saving ? "Guardando…" : "Registrar pago"}</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminWithdrawals() {
  const { user } = useAuth();
  const [filter, setFilter] = useState("pending");
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("loading");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState("");
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [minWd, setMinWd] = useState("");
  const [savingMin, setSavingMin] = useState(false);

  useEffect(() => { getSettings(user.id).then((d) => setMinWd(String(d?.min_withdrawal_usd ?? ""))).catch(() => {}); }, [user.id]);

  const saveMin = async () => {
    const v = Number(minWd);
    if (!(v > 0)) { toast.error("Mínimo inválido"); return; }
    setSavingMin(true);
    try { await updateSettings({ viewer_id: user.id, min_withdrawal_usd: v }); toast.success("Mínimo de retiro actualizado"); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSavingMin(false); }
  };

  const load = useCallback(() => {
    setStatus("loading");
    getAdminWithdrawals(user.id, filter)
      .then((d) => { setItems(d?.withdrawals || []); setStatus("ok"); })
      .catch((e) => { setMsg(e?.response?.data?.detail || "Acceso restringido"); setStatus("denied"); });
  }, [user.id, filter]);

  useEffect(() => { load(); }, [load]);

  const decide = async (id, action) => {
    setBusy(id);
    try { await decideWithdrawal(id, { viewer_id: user.id, action, note: "" }); toast.success("Retiro actualizado"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo actualizar"); }
    finally { setBusy(""); }
  };

  if (status === "loading") return <Loading />;
  if (status === "denied") return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white py-20 text-center" data-testid="admin-denied">
      <div className="mb-4 rounded-full border border-amber-300 bg-amber-50 p-4 text-amber-700"><ShieldAlert className="h-7 w-7" /></div>
      <h3 className="font-serif text-xl font-bold text-black">Solo administradores</h3>
      <p className="mt-2 max-w-sm text-sm text-slate-500">{msg}</p>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-withdrawals-page">
      <SectionTitle right={<button data-testid="open-payout" onClick={() => setPayoutOpen(true)} className="flex items-center gap-1.5 rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"><Plus className="h-4 w-4" /> Pago manual</button>}>Retiros</SectionTitle>

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid="settings-card">
        <div>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">Mínimo de retiro (USD)</label>
          <input data-testid="min-withdrawal-input" type="number" value={minWd} onChange={(e) => setMinWd(e.target.value)} className="w-40 rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-black" />
        </div>
        <button data-testid="save-min-withdrawal" onClick={saveMin} disabled={savingMin} className="rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{savingMin ? "Guardando…" : "Guardar mínimo"}</button>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f.key} data-testid={`wd-filter-${f.key || "all"}`} onClick={() => setFilter(f.key)} className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${filter === f.key ? "border-black bg-black text-white" : "border-slate-300 text-slate-600 hover:border-black"}`}>{f.label}</button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3 font-semibold">Artista</th>
                <th className="px-5 py-3 font-semibold">Método / Datos</th>
                <th className="px-5 py-3 text-right font-semibold">Monto</th>
                <th className="px-5 py-3 text-center font-semibold">Estado</th>
                <th className="px-5 py-3 text-right font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((w) => {
                const st = STATUS[w.status] || { label: w.status, cls: "border-slate-300 bg-slate-50 text-slate-600" };
                return (
                  <tr key={w.id} className="border-t border-slate-100" data-testid={`admin-wd-row-${w.id}`}>
                    <td className="px-5 py-3"><div className="font-medium text-black">{w.user_name}</div><div className="text-xs text-slate-500">{fmtDate(w.created_at)}</div></td>
                    <td className="px-5 py-3 max-w-xs"><div className="font-medium text-slate-700">{w.method}</div><div className="truncate text-[11px] text-slate-500" title={w.details}>{w.details}</div></td>
                    <td className="px-5 py-3 text-right font-mono font-semibold tabular-nums text-black">{fmtUSD(w.amount_usd)}</td>
                    <td className="px-5 py-3 text-center"><span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${st.cls}`}><Banknote className="h-3 w-3" /> {st.label}</span></td>
                    <td className="px-5 py-3">
                      <div className="flex justify-end gap-2">
                        {w.status === "pending" && <button data-testid={`wd-approve-${w.id}`} disabled={busy === w.id} onClick={() => decide(w.id, "approve")} className="flex items-center gap-1 rounded-lg border border-blue-300 bg-blue-50 px-2.5 py-1.5 text-xs font-semibold text-blue-700 disabled:opacity-50"><Check className="h-3.5 w-3.5" /> Aprobar</button>}
                        {(w.status === "pending" || w.status === "approved") && <button data-testid={`wd-paid-${w.id}`} disabled={busy === w.id} onClick={() => decide(w.id, "paid")} className="flex items-center gap-1 rounded-lg border border-emerald-300 bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 disabled:opacity-50"><DollarSign className="h-3.5 w-3.5" /> Pagado</button>}
                        {(w.status === "pending" || w.status === "approved") && <button data-testid={`wd-reject-${w.id}`} disabled={busy === w.id} onClick={() => decide(w.id, "reject")} className="flex items-center gap-1 rounded-lg border border-red-300 bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-700 disabled:opacity-50"><Ban className="h-3.5 w-3.5" /> Rechazar</button>}
                        {(w.status === "paid" || w.status === "rejected") && <span className="text-xs text-slate-400">—</span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!items.length && <tr><td colSpan={5} className="px-5 py-10 text-center text-sm text-slate-400">No hay retiros en este filtro.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {payoutOpen && <PayoutDialog viewerId={user.id} onClose={() => setPayoutOpen(false)} onDone={load} />}
    </div>
  );
}
